/**
 * The panel where a closure request is actually decided.
 *
 * Everything needed to decide is here rather than on three other screens: who asked, whether
 * we could find their account, and what that account still has in flight. That last part is
 * the reason this is a queue at all — approving is the only action in this console that
 * deactivates a live account, and the sessions and orders that outlive it are what makes it a
 * judgement rather than a click.
 *
 * A panel rather than a page, because a queue you have to navigate away from is a queue you
 * lose your place in.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Check,
  Globe,
  Loader2,
  RotateCcw,
  ShieldQuestion,
  Smartphone,
  Store,
  UserRound,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Field, FieldList } from '@/components/detail/detail-panel';
import { error_message, notify } from '@/components/ui/toaster';
import { date_time, inr } from '@/lib/format';
import { use_deletion_actions, use_deletion_detail } from '@/features/account-deletions/hooks';
import {
  days_until_purge,
  in_grace_period,
  is_unidentified,
  type AccountDeletionDetail,
} from '@/features/account-deletions/types';

type Mode = 'idle' | 'approve' | 'reject' | 'cancel';

export function DeletionDecisionSheet({
  id,
  on_close,
  on_done,
}: {
  id: string | null;
  on_close: () => void;
  on_done: (message: string) => void;
}) {
  const detail_q = use_deletion_detail(id);
  const actions = use_deletion_actions(id);
  const [mode, set_mode] = useState<Mode>('idle');
  const [text, set_text] = useState('');

  // A different request in the same panel must never inherit the last one's half-typed
  // rejection reason — that text is sent to a person verbatim.
  useEffect(() => {
    set_mode('idle');
    set_text('');
  }, [id]);

  const r = detail_q.data;

  return (
    <Sheet open={id !== null} onOpenChange={(open) => !open && on_close()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-lg" data-testid="deletion-sheet">
        <SheetHeader className="text-left">
          <SheetTitle>Deletion request</SheetTitle>
          <SheetDescription>
            {r ? `Raised ${date_time(r.created_at)}` : 'Loading…'}
          </SheetDescription>
        </SheetHeader>

        {detail_q.isLoading || !r ? (
          <div className="mt-6 flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            <StateBanner r={r} />

            <FieldList>
              <Field label="Name" value={r.contact_name} />
              <Field label="Phone" value={r.contact_phone} mono />
              <Field label="Email" value={r.contact_email} />
              <Field
                label="Account type"
                value={r.subject_type === 'VENDOR' ? 'Vendor / business' : 'Pet owner'}
              />
              <Field
                label="Came from"
                value={
                  r.source === 'PUBLIC_FORM' ? (
                    <Badge tone="info" size="sm" variant="outline">
                      <Globe className="h-3 w-3" /> Public page
                    </Badge>
                  ) : (
                    <Badge tone="neutral" size="sm" variant="outline">
                      <Smartphone className="h-3 w-3" /> In the app
                    </Badge>
                  )
                }
              />
              <Field
                label="Account"
                value={
                  is_unidentified(r) ? (
                    <Badge tone="warning" size="sm" dot>
                      <ShieldQuestion className="h-3 w-3" /> Not found
                    </Badge>
                  ) : r.subject_type === 'VENDOR' ? (
                    <Link
                      to={`/vendors/${r.vendor_profile_id}`}
                      className="inline-flex items-center gap-1.5 font-medium underline-offset-2 hover:text-primary hover:underline"
                    >
                      <Store className="h-3.5 w-3.5" /> {r.business_name ?? 'Vendor'}
                    </Link>
                  ) : (
                    <Link
                      to={`/users/${r.user_id}`}
                      className="inline-flex items-center gap-1.5 font-medium underline-offset-2 hover:text-primary hover:underline"
                    >
                      <UserRound className="h-3.5 w-3.5" /> Open the account
                    </Link>
                  )
                }
              />
              <Field label="Their reason" value={r.reason} empty="None given" />
              {r.decided_at && (
                <Field
                  label="Decided"
                  // No admin on an in-app closure, because nobody had to decide it — the
                  // account holder was signed in, which is the whole thing an approval
                  // verifies. Say so rather than leaving a bare timestamp that reads like
                  // somebody's name went missing.
                  value={`${date_time(r.decided_at)} ${
                    r.decided_by_admin_email
                      ? `by ${r.decided_by_admin_email}`
                      : 'by the account holder, from the app'
                  }`}
                />
              )}
              {r.decision_note && <Field label="Our note" value={r.decision_note} />}
            </FieldList>

            <Commitments r={r} />

            {r.status === 'PENDING' && (
              <Actions
                mode={mode}
                text={text}
                set_text={set_text}
                set_mode={set_mode}
                unidentified={is_unidentified(r)}
                busy={actions.approve.isPending || actions.reject.isPending}
                on_approve={() =>
                  run(
                    () => actions.approve.mutateAsync(text.trim() || undefined),
                    'Account closed. It will be erased after the grace period.',
                  )
                }
                on_reject={() =>
                  run(() => actions.reject.mutateAsync(text.trim()), 'Request refused.')
                }
              />
            )}

            {in_grace_period(r) && (
              <ReverseBlock
                mode={mode}
                text={text}
                set_text={set_text}
                set_mode={set_mode}
                busy={actions.cancel.isPending}
                days={days_until_purge(r)}
                on_cancel={() =>
                  run(() => actions.cancel.mutateAsync(text.trim()), 'Closure reversed — the account is open again.')
                }
              />
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );

  async function run(fn: () => Promise<unknown>, message: string) {
    try {
      await fn();
      on_done(message);
    } catch (err) {
      // Inside the panel, not as a page banner: the sheet sits above everything, so a
      // toast is the only failure message that is actually visible from here.
      notify.error(err, error_message(err));
    }
  }
}

function StateBanner({ r }: { r: AccountDeletionDetail }) {
  if (r.status === 'PENDING') {
    return is_unidentified(r) ? (
      <p
        className="rounded-panel border border-warning/40 bg-warning/10 p-3 text-data text-warning"
        data-testid="deletion-unmatched-banner"
      >
        This came from the public page and matches no account on the platform. It cannot be
        approved — verify who they are, and if there is genuinely no account here, refuse it
        saying so.
      </p>
    ) : r.source === 'PUBLIC_FORM' ? (
      <p className="rounded-panel border border-border bg-muted/50 p-3 text-data text-muted-foreground">
        This came from the public page, so nothing proves the sender owns this account. Verify
        them — a call to the number on file — before approving.
      </p>
    ) : null;
  }

  if (in_grace_period(r)) {
    const days = days_until_purge(r);
    return (
      <p className="rounded-panel border border-destructive/40 bg-destructive/10 p-3 text-data text-destructive">
        Closed and counting down. The data is erased in {days} day{days === 1 ? '' : 's'}
        {r.purge_after ? ` (${date_time(r.purge_after)})` : ''}, after which this cannot be
        reversed.
      </p>
    );
  }

  if (r.purged_at) {
    return (
      <p className="rounded-panel border border-border bg-muted/50 p-3 text-data text-muted-foreground">
        Erased on {date_time(r.purged_at)}. Nothing here can be restored.
      </p>
    );
  }
  return null;
}

/** What the account still owes or is owed — the thing the decision actually turns on. */
function Commitments({ r }: { r: AccountDeletionDetail }) {
  const c = r.open_commitments;
  const live = c.active_bookings + c.active_orders;
  const money = Number(c.net_captured_inr) > 0;

  return (
    <div>
      <h3 className="font-heading text-sm font-bold">Still in flight</h3>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <Tile label="Bookings" value={c.active_bookings} alarm={c.active_bookings > 0} />
        <Tile label="Orders" value={c.active_orders} alarm={c.active_orders > 0} />
        <Tile label="Captured" value={inr(c.net_captured_inr)} alarm={money} />
      </div>
      {live > 0 && (
        <p
          className="mt-2 flex items-start gap-1.5 text-micro text-warning"
          data-testid="deletion-commitments-warning"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Closing this account strands {live} live {live === 1 ? 'item' : 'items'}. Settle or
          cancel them first unless there is a reason not to.
        </p>
      )}
    </div>
  );
}

function Tile({ label, value, alarm }: { label: string; value: string | number; alarm: boolean }) {
  return (
    <div className="rounded-panel border border-border p-2.5">
      <p className="text-micro text-muted-foreground">{label}</p>
      <p
        className={
          'mt-0.5 font-heading text-base font-bold tabular-nums ' +
          (alarm ? 'text-warning' : 'text-foreground')
        }
      >
        {value}
      </p>
    </div>
  );
}

function Actions({
  mode,
  text,
  set_text,
  set_mode,
  unidentified,
  busy,
  on_approve,
  on_reject,
}: {
  mode: Mode;
  text: string;
  set_text: (v: string) => void;
  set_mode: (m: Mode) => void;
  unidentified: boolean;
  busy: boolean;
  on_approve: () => void;
  on_reject: () => void;
}) {
  if (mode === 'approve') {
    return (
      <ConfirmBlock
        title="Close this account"
        body="The account is deactivated immediately — every session ends, and a vendor storefront leaves discovery. The data is erased after the grace period, and until then this can be reversed from here."
        label="Note (optional)"
        placeholder="How you verified them, for the record"
        required={false}
        text={text}
        set_text={set_text}
        busy={busy}
        confirm_label="Close the account"
        tone="destructive"
        on_confirm={on_approve}
        on_back={() => {
          set_mode('idle');
          set_text('');
        }}
        test_id="approve-block"
      />
    );
  }

  if (mode === 'reject') {
    return (
      <ConfirmBlock
        title="Refuse this request"
        body="The requester is shown this reason word for word, so write it to them. They can ask again once whatever it names is sorted."
        label="Reason"
        placeholder="e.g. You have a refund in progress — please let that settle first."
        required
        text={text}
        set_text={set_text}
        busy={busy}
        confirm_label="Refuse the request"
        tone="default"
        on_confirm={on_reject}
        on_back={() => {
          set_mode('idle');
          set_text('');
        }}
        test_id="reject-block"
      />
    );
  }

  return (
    <div className="flex flex-wrap gap-2 border-t border-border pt-4">
      <Button
        variant="destructive"
        size="sm"
        className="gap-1.5"
        // The API refuses this outright on an unmatched request; disabling it here means
        // the operator learns that from the screen rather than from an error.
        disabled={unidentified}
        title={unidentified ? 'No account matched — this cannot be approved' : undefined}
        onClick={() => set_mode('approve')}
        data-testid="deletion-approve"
      >
        <Check className="h-4 w-4" /> Approve &amp; close
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => set_mode('reject')}
        data-testid="deletion-reject"
      >
        <Ban className="h-4 w-4" /> Refuse
      </Button>
    </div>
  );
}

/** The support path: take a closure back while it is still inside its window. */
function ReverseBlock({
  mode,
  text,
  set_text,
  set_mode,
  busy,
  days,
  on_cancel,
}: {
  mode: Mode;
  text: string;
  set_text: (v: string) => void;
  set_mode: (m: Mode) => void;
  busy: boolean;
  days: number | null;
  on_cancel: () => void;
}) {
  if (mode === 'cancel') {
    return (
      <ConfirmBlock
        title="Reopen this account"
        body="The account is reactivated and a suspended vendor goes back to verified. Do this when the account holder has asked you to — it is the cancellation the deletion page promises them."
        label="Reason"
        placeholder="e.g. Account holder called to say it was a mistake"
        required
        text={text}
        set_text={set_text}
        busy={busy}
        confirm_label="Reopen the account"
        tone="default"
        on_confirm={on_cancel}
        on_back={() => {
          set_mode('idle');
          set_text('');
        }}
        test_id="cancel-block"
      />
    );
  }

  return (
    <div className="border-t border-border pt-4">
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => set_mode('cancel')}
        data-testid="deletion-reverse"
      >
        <RotateCcw className="h-4 w-4" /> Reverse the closure
      </Button>
      <p className="mt-2 text-micro text-muted-foreground">
        {days !== null && days > 0
          ? `Available for another ${days} day${days === 1 ? '' : 's'}.`
          : 'The window is closing.'}
      </p>
    </div>
  );
}

function ConfirmBlock({
  title,
  body,
  label,
  placeholder,
  required,
  text,
  set_text,
  busy,
  confirm_label,
  tone,
  on_confirm,
  on_back,
  test_id,
}: {
  title: string;
  body: string;
  label: string;
  placeholder: string;
  required: boolean;
  text: string;
  set_text: (v: string) => void;
  busy: boolean;
  confirm_label: string;
  tone: 'destructive' | 'default';
  on_confirm: () => void;
  on_back: () => void;
  test_id: string;
}) {
  const ok = !required || text.trim().length >= 5;
  return (
    <div className="border-t border-border pt-4" data-testid={test_id}>
      <h3 className="font-heading text-sm font-bold">{title}</h3>
      <p className="mt-1 text-data text-muted-foreground">{body}</p>
      <label className="mt-3 block text-data font-semibold" htmlFor="decision-note">
        {label}
      </label>
      <textarea
        id="decision-note"
        rows={3}
        value={text}
        maxLength={1000}
        placeholder={placeholder}
        onChange={(e) => set_text(e.target.value)}
        className="mt-1.5 w-full rounded-control border border-input bg-background px-3 py-2 text-data shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
        data-testid="decision-note"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant={tone === 'destructive' ? 'destructive' : 'default'}
          size="sm"
          className="gap-1.5"
          disabled={!ok || busy}
          onClick={on_confirm}
          data-testid="decision-confirm"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {confirm_label}
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={on_back}>
          Back
        </Button>
      </div>
      {required && !ok && (
        <p className="mt-1.5 text-micro text-muted-foreground">
          A few words at least — this is read by a person.
        </p>
      )}
    </div>
  );
}
